import { languages, PLAINTEXT_ID } from "@codeshare/languages";
import { type ColorScheme, SHIKI_THEMES } from "@codeshare/theme";
import type * as MonacoApi from "monaco-editor/editor";
import type { Awareness } from "y-protocols/awareness";
import type * as Y from "yjs";
import { RemoteCursors } from "./collab/cursors";
import { bindText, createUndoManager, type TextBinding } from "./collab/text-binding";
import type { Highlighting } from "./highlighting";
import { monacoLanguageId } from "./language-ids";
import "./collab/cursors.css";

export type Monaco = typeof MonacoApi;
export type CodeEditor = MonacoApi.editor.IStandaloneCodeEditor;

let monacoPromise: Promise<Monaco> | undefined;
let highlighting: Highlighting | undefined;
let colorScheme: ColorScheme = "light";

/** Loads Monaco, its workers, and Shiki highlighting once, on first use. */
export function loadMonaco(): Promise<Monaco> {
  monacoPromise ??= Promise.all([import("./monaco"), import("./highlighting")]).then(
    async ([{ monaco }, { setupHighlighting }]) => {
      highlighting = await setupHighlighting(monaco, languages);
      highlighting.bridge.setTheme(SHIKI_THEMES[colorScheme]);
      // Each model's indentation: its own (detected), else its language's convention (Go: tabs).
      const indentFor = (model: MonacoApi.editor.ITextModel) => {
        const indent = languages.get(model.getLanguageId())?.config.indent;
        if (indent?.tabs) model.updateOptions({ insertSpaces: false, tabSize: indent.size ?? 4 });
        else model.detectIndentation(true, indent?.size ?? 2);
      };
      monaco.editor.onDidCreateModel((model) => {
        // One line ending for everyone in a room: \n, whatever the OS (Monaco would pick \r\n on
        // Windows for new files). Files that already use \r\n keep it.
        if (!model.getValue().includes("\r")) model.setEOL(monaco.editor.EndOfLineSequence.LF);
        indentFor(model);
        model.onDidChangeLanguage(() => indentFor(model));
      });
      // Monaco measures glyphs once; measure again when a web font (Geist Mono) finishes loading.
      globalThis.document?.fonts?.addEventListener("loadingdone", () =>
        monaco.editor.remeasureFonts(),
      );
      return monaco;
    },
  );
  return monacoPromise;
}

/**
 * Loads a language's grammar and editor config (once) and returns the Monaco language id to use
 * for models. Unknown ids resolve to "plaintext".
 */
export async function ensureLanguage(languageId: string): Promise<string> {
  await loadMonaco();
  return highlighting?.ensureLanguage(languageId) ?? PLAINTEXT_ID;
}

/** Points a model at a language, loading its grammar first. */
export async function setModelLanguage(
  model: MonacoApi.editor.ITextModel,
  languageId: string,
): Promise<void> {
  const monaco = await loadMonaco();
  const monacoId = await ensureLanguage(languageId);
  if (!model.isDisposed()) monaco.editor.setModelLanguage(model, monacoId);
}

/** Sets the Catppuccin flavor for every editor. Safe to call before Monaco has loaded. */
export function setEditorColorScheme(scheme: ColorScheme): void {
  colorScheme = scheme;
  highlighting?.bridge.setTheme(SHIKI_THEMES[scheme]);
}

function reducedMotion(): boolean {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

const MONO_FONT =
  '"Geist Mono Variable", ui-monospace, "SF Mono", "Cascadia Code", "JetBrains Mono", Menlo, monospace';

export const DEFAULT_EDITOR_OPTIONS: MonacoApi.editor.IStandaloneEditorConstructionOptions = {
  automaticLayout: true,
  fontFamily: MONO_FONT,
  fontLigatures: false,
  fontSize: 14,
  // Phones need the width for code.
  minimap: { enabled: !(globalThis.matchMedia?.("(max-width: 720px)").matches ?? false) },
  scrollBeyondLastLine: false,
  renderWhitespace: "selection",
  bracketPairColorization: { enabled: true },
  fixedOverflowWidgets: true,
  tabSize: 2,
  // VS Code's default: indentation rules apply as you type (Ruby's `end`, Python's `else:`).
  autoIndent: "full",
  // Test results (✓/✗) show in the glyph margin, as in VS Code.
  glyphMargin: true,
  // No blinking or animated scrolling for people who asked for reduced motion.
  ...(reducedMotion()
    ? { cursorBlinking: "solid", smoothScrolling: false, cursorSmoothCaretAnimation: "off" }
    : { cursorBlinking: "smooth" }),
};

export interface CreateEditorOptions {
  /** Initial text. */
  value?: string;
  /** Language id from @codeshare/languages. Omit to look it up from `path`. */
  language?: string;
  /** File path for the model URI; drives language services (e.g. `.ts` IntelliSense). */
  path?: string;
  readOnly?: boolean;
  /** Aborting before Monaco finishes loading cancels creation (e.g. on unmount). */
  signal?: AbortSignal;
  /** Extra Monaco options, merged over the defaults. */
  options?: MonacoApi.editor.IStandaloneEditorConstructionOptions;
}

export interface EditorDocument {
  value: string;
  /** File path for the model URI; drives language services. */
  path: string;
  /** Language id from @codeshare/languages. Omit to look it up from `path`. */
  language?: string;
}

export interface SharedDocument {
  /** Stable id of the file in the shared workspace (cursor awareness is per file). */
  fileId: string;
  path: string;
  text: Y.Text;
  awareness: Awareness;
  language?: string;
}

export interface EditorHandle {
  readonly monaco: Monaco;
  readonly editor: CodeEditor;
  /** Replaces the editor's document (new model at `path`); the old model is disposed. */
  setDocument(document: EditorDocument): Promise<void>;
  /**
   * Opens a collaborative document: the model mirrors `text`, selections are shared through
   * `awareness`, and undo/redo only affect this user's edits.
   */
  setSharedDocument(document: SharedDocument): Promise<void>;
  /** Disposes a shared file's model (e.g. the file was deleted). */
  closeSharedDocument(fileId: string): void;
  /** Disposes every shared file's model (e.g. switching rooms). */
  closeAllSharedDocuments(): void;
  /** Switches the current document's language, loading its grammar first. */
  setLanguage(languageId: string): Promise<void>;
  dispose(): void;
}

/**
 * Creates a Monaco editor in `container`, loading Monaco first if needed. Rejects with the
 * signal's reason if `signal` aborts while Monaco is loading.
 */
export async function createEditor(
  container: HTMLElement,
  {
    value = "",
    language,
    path = "untitled",
    readOnly = false,
    signal,
    options,
  }: CreateEditorOptions = {},
): Promise<EditorHandle> {
  const monaco = await loadMonaco();
  const monacoLanguage = await ensureLanguage(
    language ?? languages.forPath(path)?.id ?? PLAINTEXT_ID,
  );
  signal?.throwIfAborted();
  const model = monaco.editor.createModel(value, monacoLanguage, monaco.Uri.file(path));
  const editor = monaco.editor.create(container, {
    ...DEFAULT_EDITOR_OPTIONS,
    ...options,
    model,
    readOnly,
  });

  // Shared documents stay bound (and keep their undo history and view state) while open in tabs.
  interface OpenFile {
    text: Y.Text;
    path: string;
    model: MonacoApi.editor.ITextModel;
    binding: TextBinding;
    undo: Y.UndoManager;
    viewState: MonacoApi.editor.ICodeEditorViewState | null;
  }
  const openFiles = new Map<string, OpenFile>();
  let activeFileId: string | undefined;
  let cursors: RemoteCursors | undefined;

  const closeFile = (fileId: string) => {
    const file = openFiles.get(fileId);
    if (!file) return;
    if (activeFileId === fileId) {
      cursors?.dispose();
      cursors = undefined;
      activeFileId = undefined;
      editor.setModel(null);
    }
    file.binding.dispose();
    file.undo.destroy();
    file.model.dispose();
    openFiles.delete(fileId);
  };
  const closeAllFiles = () => {
    for (const fileId of [...openFiles.keys()]) closeFile(fileId);
  };

  // Ctrl/Cmd+Z and Shift+Ctrl/Cmd+Z (and Ctrl+Y) go to the collaborative undo manager when sharing.
  const { KeyMod, KeyCode } = monaco;
  const activeUndo = () => (activeFileId ? openFiles.get(activeFileId)?.undo : undefined);
  const undo = () => {
    const manager = activeUndo();
    if (manager) manager.undo();
    else editor.trigger("keyboard", "undo", null);
  };
  const redo = () => {
    const manager = activeUndo();
    if (manager) manager.redo();
    else editor.trigger("keyboard", "redo", null);
  };
  editor.addCommand(KeyMod.CtrlCmd | KeyCode.KeyZ, undo);
  editor.addCommand(KeyMod.CtrlCmd | KeyMod.Shift | KeyCode.KeyZ, redo);
  editor.addCommand(KeyMod.CtrlCmd | KeyCode.KeyY, redo);

  return {
    monaco,
    editor,
    async setSharedDocument(document) {
      const wanted = document.language ?? languages.forPath(document.path)?.id ?? PLAINTEXT_ID;
      const loading = ensureLanguage(wanted);
      // Switching from another file: switch synchronously. The tab already shows this file, so
      // typing must land in it, not in the previous one while a grammar loads; highlighting
      // follows once it's loaded. The first file waits for its grammar instead (nothing else is
      // open to type into), so highlighting and language features start together.
      if (activeFileId === undefined) await loading;
      const registered = monaco.languages
        .getLanguages()
        .some((l) => l.id === monacoLanguageId(wanted));
      const languageId = registered ? monacoLanguageId(wanted) : "plaintext";
      let file = openFiles.get(document.fileId);
      // A rename (new URI) or a different document (joined another room) needs a fresh model.
      if (file && (file.path !== document.path || file.text !== document.text)) {
        closeFile(document.fileId);
        file = undefined;
      }
      if (!file) {
        const uri = monaco.Uri.file(document.path);
        monaco.editor.getModel(uri)?.dispose();
        const model = monaco.editor.createModel(document.text.toString(), languageId, uri);
        file = {
          text: document.text,
          path: document.path,
          model,
          binding: bindText(document.text, model),
          undo: createUndoManager(document.text),
          viewState: null,
        };
        openFiles.set(document.fileId, file);
      }
      if (activeFileId !== document.fileId) {
        const previous = activeFileId ? openFiles.get(activeFileId) : undefined;
        if (previous) previous.viewState = editor.saveViewState();
        cursors?.dispose();
        editor.setModel(file.model);
        if (file.viewState) editor.restoreViewState(file.viewState);
        activeFileId = document.fileId;
        cursors = new RemoteCursors({
          monaco,
          editor,
          awareness: document.awareness,
          ytext: document.text,
          fileId: document.fileId,
        });
      }
      const opened = file;
      const loaded = await loading;
      if (!opened.model.isDisposed()) monaco.editor.setModelLanguage(opened.model, loaded);
    },
    closeSharedDocument(fileId) {
      closeFile(fileId);
    },
    closeAllSharedDocuments() {
      closeAllFiles();
    },
    async setDocument(document) {
      const languageId = await ensureLanguage(
        document.language ?? languages.forPath(document.path)?.id ?? PLAINTEXT_ID,
      );
      closeAllFiles();
      const uri = monaco.Uri.file(document.path);
      const previous = editor.getModel();
      if (previous?.uri.toString() === uri.toString()) {
        previous.setValue(document.value);
        monaco.editor.setModelLanguage(previous, languageId);
        return;
      }
      monaco.editor.getModel(uri)?.dispose();
      editor.setModel(monaco.editor.createModel(document.value, languageId, uri));
      previous?.dispose();
    },
    async setLanguage(languageId) {
      const current = editor.getModel();
      if (current) await setModelLanguage(current, languageId);
    },
    dispose() {
      closeAllFiles();
      editor.getModel()?.dispose();
      editor.dispose();
    },
  };
}
